import { SlideTimerIndicator } from "@components/atoms/SlideTimerIndicator"
import { Button } from "@components/ui/Button"
import { SliderTimerProvider, useSlideTimer } from "@hooks/useSliderTimer"
import { useDrag } from "@use-gesture/react"
import { cx, tv } from "@utils/tw"
import { defineBlock, InnerBlocks, SingleContentBlock, useInnerBlocks } from "eddev/blocks"
import { ClientOnly } from "eddev/routing"
import { AnimatePresence, motion, useAnimationFrame } from "motion/react"
import { useRef, useState } from "react"

export const meta: BlockMeta = {
  title: "Homepage Carousel",
  icon: "slides",
  category: "layouts",
  inserter: false,
  tags: ["#homepage"],
  flags: {
    fullWidth: true,
  },
}

export default defineBlock("homepage/homepage-carousel", (props) => {
  const heroLinks = (
    <div className="flex gap-3 md:gap-grid-gutter w-max md:w-full">
      {props.heroLinks?.map((item, i) => (
        <div key={i} className="flex-1">
          <Button href={item?.link?.url!} target={item?.link?.target} shape="branded" variant="hero" className="w-full">
            <span dangerouslySetInnerHTML={{ __html: item?.link?.title ?? "" }} />
          </Button>
        </div>
      ))}
    </div>
  )

  if (env.admin) {
    return (
      <div className="bg-neutral-900">
        <InnerBlocks
          orientation="horizontal"
          allowedBlocks={["homepage/homepage-carousel-item"]}
          appender={{ type: "button" }}
          adminClassName="overflow-x-auto snap-x snap-mandatory relative w-grid-outer mx-auto h-cols-10 max-h-[60vh] flex gap-3 [&>.wp-block]:relative [&_>_.wp-block]:h-full [&_>_.wp-block]:w-grid-outer [&>.wp-block]:flex-none [&>.wp-block]:snap-start"
        />
        <div className="py-4 w-grid mx-auto">{heroLinks}</div>
      </div>
    )
  }

  const [enabled, setEnabled] = useState(false)

  const slides = useInnerBlocks()

  const slideTimer = useSlideTimer({
    total: slides.length,
    slideDuration: 8,
    enabled,
  })

  const container = useRef<HTMLDivElement>(null)
  const scrim = useRef<HTMLDivElement>(null)

  const [outOfView, setOutOfView] = useState(false)

  useAnimationFrame(() => {
    const rect = container.current?.getBoundingClientRect()!
    if (!rect) return
    const progress = Math.max(0.005, Math.pow(Math.max(0, Math.min(1, -(rect.y - 100) / (rect.height * 0.9))), 1.2))
    scrim.current?.style.setProperty("--scrim-progress", progress.toString())
    setEnabled(progress < 0.8)
    setOutOfView(rect.y + rect.height < 50)
  })

  useDrag(
    (e) => {
      if (e.first) {
        slideTimer.pause()
      } else if (e.last) {
        slideTimer.start()
      }
      if (e.swipe && e.last) {
        if (e.swipe[0] > 0) {
          slideTimer.goPrev()
        } else if (e.swipe[0] < 0) {
          slideTimer.goNext()
        }
      }
    },
    {
      target: container,
      axis: "x",
      swipe: {
        distance: 50,
        duration: 500,
      },
    },
  )

  return (
    <section data-mixpanel-block="Homepage Carousel" style={{ opacity: outOfView ? 0 : 1 }}>
      <div ref={container} className={cx(sizer(), "relative touch-pan-y md:-mb-5")}>
        <div
          className={cx(
            sizer(),
            "fixed isolate w-full left-0 top-[--headerBottom] flex items-center justify-center will-change-transform",
          )}
        >
          <div className="absolute top-0 left-0 right-0 z-[2]">
            <div className="w-grid mx-auto h-8 flex items-center">
              <SlideTimerIndicator timer={slideTimer} />
            </div>
          </div>
          <SliderTimerProvider value={slideTimer}>
            <AnimatePresence mode="wait">
              <motion.div key={slideTimer.current} className="w-full h-full relative z-[1]">
                <SingleContentBlock block={slides[slideTimer.current]} />
              </motion.div>
            </AnimatePresence>
          </SliderTimerProvider>
        </div>
        {/* Gradient which scrolls up/fades in with the carousel */}
        <ClientOnly>
          <div className="absolute top-0 left-0 w-full -bottom-10 pointer-events-none">
            <div
              ref={scrim}
              className="absolute top-0 left-0 w-full bottom-0 bg-scrimUp opacity-[pow(var(--scrim-progress),0.3)] pointer-events-none"
            >
              <div className="absolute inset-0 bg-bg opacity-[pow(var(--scrim-progress),0.3)]"></div>
            </div>
            {/* Desktop hero links */}
            <div className="hidden md:block absolute left-0 right-0 bottom-10 z-10 py-5 pointer-events-auto">
              <div className="w-grid mx-auto" data-mixpanel-area="Homepage Quick Links">
                {heroLinks}
              </div>
            </div>
          </div>
        </ClientOnly>
      </div>
      {/* Mobile hero links */}
      <div className="md:hidden z-50 pt-6 pb-10 bg-bg will-change-transform" data-mixpanel-area="Homepage Quick Links">
        <div className="relative w-full overflow-auto scrollbars-hidden z-10 px-grid-margin">{heroLinks}</div>
        <div className="absolute w-full bottom-full h-10 bg-scrimUp pointer-events-none"></div>
      </div>
      {/*
        Spacer under the carousel, which helps cover up the area between the carousel and the rest of the homepage content.
        Mostly just a fallback in case JS hasn't loaded yet and the user has started scrolling.
      */}
      <div className="max-md:hidden relative w-full h-10 bg-scrimUp pointer-events-none"></div>
    </section>
  )
})

const sizer = tv({
  base: "w-full h-[90vw] md:h-[calc(100vh-var(--spacing-headerHeight))]",
})
